// Pure, DOM-free wording for "what is the agent DOING right now" — the plain-English
// phrase a WATCH surface (the session's LIVE block, the Progress rail) puts on a tool call.
//
// THE DEFECT THIS EXISTS FOR, measured verbatim on the session's right rail:
//
//     mcp__cyborg7__cyborg7_list_states
//     {"projectId":"tp_79f8df2b-0a59-467e-a9f6-eebcd05c3f1b"}
//
// A raw MCP call id followed by its raw JSON arguments. Two separate leaks, and both come
// from the activity curator's external-tool branch (activity-curator.ts curateToolCall):
// `humanizeToolName` returns a `__`-delimited name UNCHANGED, and a namespaced tool's
// summary is deliberately the JSON input. That branch is a faithful port of Paseo's own
// curator and is locked by its own tests, so it is left alone — the WATCH surfaces read
// this module instead. The repo's own QA standard for these rows is "title = what it does,
// not the raw output", and a `mcp__…` identifier plus a project UUID fails it twice.
//
// Two fields, never one glued string, because both consumers already render two slots (the
// rail's `label` + `text`, the live block's action line):
//   action — the plain phrase: "Reading the board's states". NEVER empty, and NEVER a raw
//            `mcp__…` identifier, whatever the input.
//   object — the thing it is acting on, WHEN the arguments name one in human terms
//            ("#build", "auth flow", "login.ts"). "" when they don't.
//
// OPAQUE IDS ARE NEVER SHOWN. `{"projectId":"tp_79f8df2b-…"}` tells a reader nothing, so no
// `*Id` key is a candidate for `object` — that call reads simply "Reading the board's
// states", which is the whole of what happened.
// The out-of-dir placeholder's `plain_text` label, the only thing that identifies it on the
// wire. Must equal OUT_OF_DIR_MARKER_LABEL in server/src/server/cyborg/cockpit-dir-scope.ts.
export const OUT_OF_DIR_MARKER_LABEL = "[activity outside shared workspace]";
// A label is a HEADER: one line, framing stripped, at most a provider's summary cap. A body
// is refused, never truncated (tool-call-label.ts tells why).
const HEADER_LABEL_MAX = 160;
function stripMachineFraming(value) {
    return value
        .replace(/\[[a-z][a-z0-9_-]*:[\s\S]*?\]/gi, " ")
        .replace(/<\/?[a-z][a-z0-9_-]*\s*\/?>/gi, " ");
}
// The header-shaped form of a free-text field, or null when it isn't one.
export function headerLabel(value) {
    if (typeof value !== "string")
        return null;
    const stripped = stripMachineFraming(value).trim();
    if (!stripped)
        return null;
    if (stripped.includes("\n") || stripped.length > HEADER_LABEL_MAX)
        return null;
    return stripped.replace(/[ \t]+/g, " ");
}
export const OUT_OF_DIR_ACTION = "Working outside the shared workspace";
// The last thing the agent could possibly be doing. Reached only by a nameless tool call
// (a malformed frame) — a blank row is worse than a vague one.
export const FALLBACK_ACTION = "Working";
// An object is a HEADER, not a body — one short line naming the thing.
const MAX_OBJECT_CHARS = 80;
// The provider framing headerLabel exists to refuse: a bracketed annotation keyed by an
// identifier (`[harness: …]`) and control tags (`<task-notification>`). Matched here as
// well so a line that headerLabel rejects on LENGTH can still be told apart from one that
// is merely long — see clampObject.
const MACHINE_FRAMING = /\[[a-z][a-z0-9_-]*:|<\/?[a-z][a-z0-9_-]*\s*\/?>/i;
function clampObject(value) {
    if (typeof value !== "string")
        return "";
    // One line only. A tool argument that is really a body (a message draft, a subagent's
    // report) contributes its first line and nothing more.
    const firstLine = value.split("\n", 1)[0];
    const header = headerLabel(firstLine);
    if (header) {
        return header.length > MAX_OBJECT_CHARS ? `${header.slice(0, MAX_OBJECT_CHARS - 1)}…` : header;
    }
    // headerLabel refused. On a single line that can only be length — and a long shell
    // command or URL is legitimate, so it is clamped rather than dropped. A line carrying
    // provider framing is refused OUTRIGHT and never truncated: the first 80 characters of
    // a harness envelope is the leak, not a smaller version of it.
    const plain = firstLine.trim().replace(/[ \t]+/g, " ");
    if (!plain || MACHINE_FRAMING.test(plain))
        return "";
    return plain.length > MAX_OBJECT_CHARS ? `${plain.slice(0, MAX_OBJECT_CHARS - 1)}…` : plain;
}
function fileName(filePath) {
    // POSIX and Windows separators both — a daemon on Windows must surface the basename,
    // not `C:\…\foo.ts` (same rule as the activity curator's lastPathSegment).
    const cleaned = filePath.replace(/[/\\]+$/, "");
    const segment = cleaned.split(/[/\\]/).pop();
    return segment && segment.length > 0 ? segment : filePath;
}
// ── The MCP leaf ─────────────────────────────────────────────────────────────────────
//
// `mcp__cyborg7__cyborg7_list_states` → `list_states`. Mirrors toolDisplayName's split
// (tool-call-label.ts) but stops one step earlier: that helper returns the DISPLAY form
// ("List states") while the table below is keyed on the raw snake_case leaf. Kept separate
// rather than refactoring toolDisplayName, whose output is locked by its own tests and by
// every collapsed tool card on every surface.
function mcpLeaf(name) {
    if (!name.startsWith("mcp__"))
        return null;
    const parts = name.split("__");
    if (parts.length < 3)
        return null;
    const server = parts[1];
    let tool = parts.slice(2).join("__");
    if (!server || !tool)
        return null;
    // Drop a redundant server prefix on the tool (`cyborg7_list_states` → `list_states`).
    if (tool.startsWith(`${server}_`))
        tool = tool.slice(server.length + 1);
    return tool || null;
}
// Tool names are chosen by whoever wrote the MCP server, so a leaf can be `constructor`,
// `toString` or `__proto__`. A bare `TABLE[leaf]` would hand back Object.prototype's
// function for those; only the table's OWN entries count.
function lookup(table, key) {
    return Object.prototype.hasOwnProperty.call(table, key) ? table[key] : undefined;
}
// ── The phrase table ─────────────────────────────────────────────────────────────────
//
// Keyed on the MCP leaf, covering every tool the daemon actually exposes to a cybo (the
// `cyborg7_*` set registered in packages/server/src/server/cyborg/). This is DATA, not
// logic: a tool that isn't here still gets a readable phrase from the generic rule below,
// so the table can never be the reason a row goes raw or blank.
export const MCP_ACTION_PHRASES = {
    // Tasks board
    list_states: "Reading the board's states",
    list_projects: "Reading the board's departments",
    list_tasks: "Reading the board's tasks",
    get_task: "Reading a task",
    create_task: "Creating a task",
    update_task: "Updating a task",
    bulk_update_tasks: "Updating several tasks",
    delete_task: "Deleting a task",
    archive_task: "Archiving a task",
    // Channels, threads, messages
    list_channels: "Reading the channel list",
    list_channel_members: "Reading a channel's members",
    create_channel: "Creating a channel",
    read_channel: "Reading a channel",
    get_channel_history: "Reading a channel's history",
    read_thread: "Reading a thread",
    send_message: "Sending a message",
    reply_in_thread: "Replying in a thread",
    react: "Reacting to a message",
    // Pages and docs
    list_pages: "Reading the page list",
    read_page: "Reading a page",
    create_page: "Writing a new page",
    update_page: "Updating a page",
    nest_page: "Moving a page",
    read_docs: "Reading the docs",
    read_source: "Reading the source",
    // Memory
    remember: "Saving something to memory",
    recall: "Looking through its memory",
    forget: "Forgetting a memory",
    update_memory: "Updating a memory",
    // Sessions
    list_my_sessions: "Reading its own sessions",
    read_session: "Reading a session",
    search_sessions: "Searching past sessions",
    start_work_session: "Starting a work session",
    // Terminals
    open_terminal: "Opening a terminal",
    close_terminal: "Closing a terminal",
    list_terminals: "Reading the open terminals",
    // Browser
    browse_open: "Opening the browser",
    browse_navigate: "Opening a page in the browser",
    browse_read_page: "Reading the page in the browser",
    browse_click: "Clicking in the browser",
    browse_type: "Typing in the browser",
    browse_scroll: "Scrolling the browser",
    browse_screenshot: "Taking a screenshot",
    browse_attach_file: "Attaching a file in the browser",
    browse_find: "Looking for something on the page",
    browse_select: "Choosing an option in the browser",
    browse_fill_form: "Filling in a form in the browser",
    browse_wait_for: "Waiting for the page",
    browse_extract: "Reading part of the page",
    browse_zoom: "Zooming into the page",
    browse_tabs: "Switching browser tabs",
    browse_press_key: "Pressing a key in the browser",
    browse_batch: "Running browser steps",
    browse_request_human: "Asking for help in the browser",
    browse_wait_human: "Waiting for a person in the browser",
    browse_close: "Closing the browser",
    // Schedules
    schedule_list: "Reading the schedules",
    schedule_get: "Reading a schedule",
    schedule_create: "Creating a schedule",
    schedule_update: "Updating a schedule",
    schedule_delete: "Deleting a schedule",
    schedule_set_enabled: "Turning a schedule on or off",
    // Workspace and the rest
    search: "Searching the workspace",
    get_workspace_roster: "Reading the workspace roster",
    list_integrations: "Reading the integrations",
    offer_integration: "Offering an integration",
    propose_actions: "Proposing actions",
    decline: "Declining",
    update_my_personality: "Updating its own personality",
    // Slack, as the person asking (cyborg7-mcp-tools.ts)
    read_slack_channel: "Reading a Slack channel",
    list_slack_dms: "Reading Slack direct messages",
    my_slack_account: "Checking the Slack account",
    find_slack_people: "Looking up people in Slack",
    send_slack_message: "Sending a Slack message",
};
// ── The NATIVE tools ─────────────────────────────────────────────────────────────────
//
// A provider's own tools, keyed LOWERCASE on the exact call name. Reached only when the
// call arrived WITHOUT a typed detail — a typed one is already phrased by the switch at
// the bottom, and the wording here is kept identical to it on purpose (a `read` detail and
// a bare `Read` envelope must not read differently for the same act).
//
// WHAT THIS IS FOR, measured live in the owner's workspace: the row rendered
//
//     LIVE | QA Deploy Check is thinking... | Power shell | (Get-ChildItem -LiteralPath …
//
// "PowerShell" is one word and a proper noun. It reached the generic rule below, which
// used to split camelCase — so it came out as two words with the second one lowercased.
// Every compound provider name had the same fate waiting (TodoWrite, WebFetch,
// NotebookEdit, BashOutput). The split is gone (see genericActionPhrase), and the names a
// person would still have to decode are answered here with WHAT THEY DO instead: the
// owner's standard is that a person understands the row, and "Running a command" beats
// "PowerShell" when the command itself is printed right after it.
//
// `PowerShell` is not a name this repo registers anywhere — it comes off the provider at
// runtime. So the generic rule has to be safe for names we have never seen, and the table
// is the readability layer on top, not the thing holding the line.
export const NATIVE_ACTION_PHRASES = {
    // Shell
    bash: "Running a command",
    powershell: "Running a command",
    bashoutput: "Checking a running command",
    // Files
    read: "Reading a file",
    write: "Writing a file",
    edit: "Editing a file",
    multiedit: "Editing a file",
    notebookedit: "Editing a notebook",
    glob: "Looking for files",
    grep: "Searching the code",
    // Web
    websearch: "Searching the web",
    webfetch: "Opening a web page",
    // Planning and delegation. The same phrase the typed `sub_agent` detail uses — a Task
    // whose card kept only the bare envelope must not read differently from one that kept
    // its detail.
    task: "Delegating to a subagent",
    agent: "Delegating to a subagent",
    workflow: "Delegating to a subagent",
    "sub-agent": "Delegating to a subagent",
    todowrite: "Updating its task list",
    exitplanmode: "Presenting its plan",
    askuserquestion: "Asking a question",
    toolsearch: "Looking for the right tool",
};
// The generic rule for a tool nobody has written a phrase for — an MCP server we don't
// ship, a native tool that arrived without a typed detail. Tool names are already
// verb-first (`execute_sql`, `list_widgets`), so the leading word becomes the verb and the
// rest becomes the thing.
export const VERB_PHRASES = {
    list: "Reading",
    get: "Reading",
    read: "Reading",
    fetch: "Fetching",
    search: "Searching",
    find: "Searching",
    query: "Querying",
    create: "Creating",
    add: "Adding",
    write: "Writing",
    update: "Updating",
    edit: "Editing",
    set: "Updating",
    delete: "Deleting",
    remove: "Deleting",
    send: "Sending",
    open: "Opening",
    close: "Closing",
    run: "Running",
    execute: "Running",
    start: "Starting",
    stop: "Stopping",
};
function capitalize(value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
}
// The fallback for a name nobody has phrased. Splits on EXPLICIT separators only —
// underscore, hyphen, dot, space — and never on a camelCase boundary.
//
// THE BUG THAT REMOVED THE CAMEL SPLIT. It used to run `([a-z0-9])([A-Z])` → "$1 $2" and
// lowercase the result, which turned the provider's real `PowerShell` tool into
// "Power shell" on screen: two words, wrong case, a proper noun taken apart. That is not
// one bad string, it is what the rule does to EVERY compound name it has not been taught
// — and tool names come off the provider at runtime, so there will always be ones we have
// never seen. A name we cannot parse is left exactly as its author wrote it: unreadable
// is a smaller failure than mangled, and "PowerShell" was never unreadable to begin with.
//
// `execute_sql` → "Running sql" still works, because THAT split is explicit and asked for.
function genericActionPhrase(base) {
    const parts = base
        .trim()
        .split(/[\s._-]+/)
        .filter((part) => part.length > 0);
    if (parts.length === 0)
        return FALLBACK_ACTION;
    const verb = lookup(VERB_PHRASES, parts[0].toLowerCase());
    // Only conjugate when there is really something to conjugate ABOUT. A verb-only name
    // ("search", "Edit") has no object word, and "Editing" alone says less than the tool's
    // own name does — those keep the name.
    if (verb && parts.length > 1)
        return `${verb} ${parts.slice(1).join(" ")}`;
    const name = parts.join(" ");
    // Already carries capitals ⇒ its author cased it deliberately (PowerShell, TodoWrite):
    // print it verbatim. An all-lowercase leaf (`now`, `weird`) just needs a capital.
    return /[A-Z]/.test(name) ? name : capitalize(name);
}
// ── The object, read off the raw arguments ───────────────────────────────────────────
//
// Only keys whose value a PERSON can read. Ordered most-identifying first, so a call
// carrying both a query and a body surfaces the query. Every candidate goes through
// headerLabel, which refuses a multi-line body and a provider's machine framing — the
// same guard that keeps a subagent's whole report out of a tool card's header.
//
// Deliberately NOT here: anything ending in `Id`/`_id`. `tp_79f8df2b-0a59-467e-…` is the
// literal string the owner was shown, and it names nothing.
const OBJECT_KEYS = [
    // A browse step's own one-line summary — and it outranks `text`, which is what was typed.
    "action_summary",
    "query",
    "pattern",
    "q",
    "command",
    "url",
    "channel",
    "channelName",
    "channel_name",
    "branch",
    "filePath",
    "file_path",
    "path",
    "title",
    "name",
    "subject",
    "slug",
    // A `Task` that kept only its bare envelope carries the brief here. Same preference
    // order the typed `sub_agent` branch uses (type first, then description), so the two
    // shapes of the same call name their subagent the same way.
    "subagent_type",
    "description",
    "message",
    "text",
    "prompt",
    "content",
];
function objectFromInput(input) {
    if (typeof input !== "object" || input === null || Array.isArray(input))
        return "";
    const record = input;
    for (const key of OBJECT_KEYS) {
        const value = record[key];
        if (typeof value !== "string")
            continue;
        const clamped = clampObject(value);
        if (clamped)
            return clamped;
    }
    return "";
}
// A tool NAME as words, for the object of a ToolSearch: `WebFetch` → "web fetch",
// `mcp__cyborg7__cyborg7_list_tasks` → "list tasks". Proper nouns keep their casing.
const PROPER_TOOL_NAMES = new Set(["PowerShell"]);
function toolNameWords(raw) {
    const name = raw.trim();
    if (PROPER_TOOL_NAMES.has(name))
        return name;
    const base = mcpLeaf(name) ?? name.replace(/^mcp__/, "");
    return base
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[_-]+/g, " ")
        .trim()
        .toLowerCase();
}
// ToolSearch's query is `select:WebFetch,Bash` when it loads named tools, else keywords.
function toolSearchObject(input) {
    const query = typeof input === "object" && input !== null ? input.query : null;
    if (typeof query !== "string")
        return "";
    const selected = /^select:(.*)$/is.exec(query.trim());
    if (!selected)
        return clampObject(query);
    const names = selected[1].split(",").map(toolNameWords).filter(Boolean);
    return clampObject(names.join(", "));
}
// A tool call with no typed detail: an MCP tool, or a native one whose adapter emitted the
// bare `unknown` envelope. This is the branch the reported defect lived in.
function untypedAction(name, input) {
    const leaf = mcpLeaf(name);
    // `mcp__weird` (malformed, no leaf) must still not render its prefix — strip it and
    // humanize whatever is left, so no path out of this module can return a raw id.
    const base = leaf ?? name.replace(/^mcp__/, "");
    const action = (leaf ? lookup(MCP_ACTION_PHRASES, leaf) : undefined) ??
        lookup(NATIVE_ACTION_PHRASES, base.trim().toLowerCase()) ??
        genericActionPhrase(base);
    const object = base.trim().toLowerCase() === "toolsearch" ? toolSearchObject(input) : objectFromInput(input);
    return { action: action || FALLBACK_ACTION, object };
}
// The provider stamps WHICH search it was; a web search used to read "Searching the code".
function searchAction(toolName) {
    if (toolName === "web_search")
        return "Searching the web";
    if (toolName === "glob")
        return "Looking for files";
    return "Searching the code";
}
/**
 * The plain-words action for one tool call. Total: every input returns a readable
 * `action`, including a nameless one.
 */
export function toolAction(entry) {
    const detail = entry.detail;
    if (!detail)
        return untypedAction(entry.name, undefined);
    switch (detail.type) {
        case "shell":
            return { action: "Running a command", object: clampObject(detail.command) };
        case "read":
            return { action: "Reading a file", object: fileName(detail.filePath) };
        case "edit":
            return { action: "Editing a file", object: fileName(detail.filePath) };
        case "write":
            return { action: "Writing a file", object: fileName(detail.filePath) };
        case "search":
            return { action: searchAction(detail.toolName), object: clampObject(detail.query) };
        case "fetch":
            return { action: "Opening a web page", object: clampObject(detail.url) };
        case "plan":
            return { action: "Working out a plan", object: clampObject(detail.text) };
        case "worktree_setup":
            return { action: "Setting up a worktree", object: clampObject(detail.branchName) };
        case "sub_agent":
            return {
                action: "Delegating to a subagent",
                object: clampObject(detail.subAgentType) || clampObject(detail.description),
            };
        case "plain_text": {
            // A synthetic call (task_notification and friends) already carries its own human
            // line — that IS the action. A label the shape guard refuses is a result body, and
            // falls through to the name like everywhere else.
            // The out-of-dir placeholder is never shown as a label (CYBORG-1522): the now row says
            // what kind of step it is, as the stream's quiet line does.
            if (detail.label === OUT_OF_DIR_MARKER_LABEL)
                return { action: OUT_OF_DIR_ACTION, object: "" };
            const label = headerLabel(detail.label) ?? headerLabel(detail.text);
            return label ? { action: label, object: "" } : untypedAction(entry.name, undefined);
        }
        case "unknown":
            return untypedAction(entry.name, detail.input);
        default:
            return untypedAction(entry.name, undefined);
    }
}
// ── Past tense, for a record of what already happened (Settings → Logs) ──────────────
//
// Every phrase above leads with a gerund. Keyed on that first word; a word not listed
// here (a plain_text label, a tool's own name) is left exactly as it was.
const PAST_TENSE = {
    Adding: "Added",
    Archiving: "Archived",
    Asking: "Asked",
    Attaching: "Attached",
    Checking: "Checked",
    Choosing: "Chose",
    Clicking: "Clicked",
    Closing: "Closed",
    Creating: "Created",
    Declining: "Declined",
    Delegating: "Delegated",
    Deleting: "Deleted",
    Editing: "Edited",
    Fetching: "Fetched",
    Filling: "Filled",
    Forgetting: "Forgot",
    Looking: "Looked",
    Moving: "Moved",
    Offering: "Offered",
    Opening: "Opened",
    Presenting: "Presented",
    Pressing: "Pressed",
    Proposing: "Proposed",
    Querying: "Queried",
    Reacting: "Reacted",
    Reading: "Read",
    Replying: "Replied",
    Running: "Ran",
    Saving: "Saved",
    Scrolling: "Scrolled",
    Searching: "Searched",
    Sending: "Sent",
    Setting: "Set",
    Starting: "Started",
    Stopping: "Stopped",
    Switching: "Switched",
    Taking: "Took",
    Turning: "Turned",
    Typing: "Typed",
    Waiting: "Waited",
    Updating: "Updated",
    Working: "Worked",
    Writing: "Wrote",
    Zooming: "Zoomed",
};
/** "Reading a file" → "Read a file". The bare fallback "Working" says nothing in the past. */
export function pastTensePhrase(action) {
    if (action === FALLBACK_ACTION)
        return "Ran a step";
    const space = action.indexOf(" ");
    const first = space === -1 ? action : action.slice(0, space);
    const past = lookup(PAST_TENSE, first);
    return past ? `${past}${space === -1 ? "" : action.slice(space)}` : action;
}
/** toolAction, told as something that already happened. */
export function toolActionPast(entry) {
    const present = toolAction(entry);
    return { action: pastTensePhrase(present.action), object: present.object };
}
// ── A permission request's title (the card, the activity alert) ──────────────────────
//
// Claude sends no title for a tool request, and some providers send the raw call id as one,
// so the card read `mcp__cyborg7__cyborg7_read_slack_channel`. A human title is kept; a
// missing or raw one is the action, asked as a request: "Read a Slack channel".
const BASE_FORM = {
    Adding: "Add",
    Archiving: "Archive",
    Asking: "Ask",
    Attaching: "Attach",
    Checking: "Check",
    Choosing: "Choose",
    Clicking: "Click",
    Closing: "Close",
    Creating: "Create",
    Declining: "Decline",
    Delegating: "Delegate",
    Deleting: "Delete",
    Editing: "Edit",
    Fetching: "Fetch",
    Filling: "Fill",
    Forgetting: "Forget",
    Looking: "Look",
    Moving: "Move",
    Offering: "Offer",
    Opening: "Open",
    Presenting: "Present",
    Pressing: "Press",
    Proposing: "Propose",
    Querying: "Query",
    Reacting: "React",
    Reading: "Read",
    Replying: "Reply",
    Running: "Run",
    Saving: "Save",
    Scrolling: "Scroll",
    Searching: "Search",
    Sending: "Send",
    Setting: "Set",
    Starting: "Start",
    Stopping: "Stop",
    Switching: "Switch",
    Taking: "Take",
    Turning: "Turn",
    Typing: "Type",
    Waiting: "Wait",
    Updating: "Update",
    Working: "Work",
    Writing: "Write",
    Zooming: "Zoom",
};
export function permissionRequestTitle(request) {
    const title = request.title?.trim();
    const rawId = title?.startsWith("mcp__") ?? false;
    if (title && title !== request.name?.trim() && !rawId)
        return title;
    // ACP names a call by its kind ("other") and keeps the MCP id only in the title, so the words
    // and the server come from that id.
    const name = (rawId ? title : request.name) ?? "";
    const { action } = toolAction({ name, detail: request.detail });
    // "Working" alone (a nameless call) says nothing as a request.
    if (action === FALLBACK_ACTION)
        return "Use a tool";
    const space = action.indexOf(" ");
    const first = space === -1 ? action : action.slice(0, space);
    const base = lookup(BASE_FORM, first);
    const asked = base ? `${base}${space === -1 ? "" : action.slice(space)}` : action;
    // Another server's tool is named with its server, so it never passes for one of Cyborg's own.
    const server = name.startsWith("mcp__") ? name.split("__")[1] : undefined;
    return server && server !== "cyborg7" ? `${asked} (${server})` : asked;
}
//# sourceMappingURL=index.js.map